'use client';

/**
 * NotificationDrawer.tsx — full-height notification drawer (høyre side)
 *
 * Erstatter HeaderPanel-bruken for varsler. Layout per Carbon
 * notification-panel-mønsteret (referanse: IBM Partner Plus drawer):
 *
 *   ┌──────────────────────────┐
 *   │ Varsler                  │  1. Tittelrad
 *   ├──────────────────────────┤
 *   │ 3 uleste  Merk alle lest │  2. Ulest-rad (kun når uleste > 0)
 *   ├──────────────────────────┤
 *   │ ● Ny chatmelding         │  3. Varselliste (rullbar)
 *   │   23. sep. 2026          │
 *   │ ● Ny chatmelding         │
 *   │   23. sep. 2026          │
 *   ├──────────────────────────┤
 *   │        Vis alle          │  4. Footer
 *   └──────────────────────────┘
 *
 * Posisjon: fixed, forankret høyre, full høyde under headeren
 * (calc(100vh - var(--cds-header-height))) — eksakt som referansens
 * "height: calc(100vh - 47px)".
 *
 * A11y:
 * - role="dialog" + aria-modal="false" (ikke-modale drawer — innholdet
 *   bak forblir tilgjengelig, som Carbon SidePanel non-modal)
 * - Escape lukker drawer-en
 * - role="log" + aria-live="polite" på listen: nye varsler annonseres
 * - Fokus flyttes til drawer-en ved åpning, tilbake til trigger ved lukking
 *
 * Data: leses fra notifications-store (singleton, SSE-drevet).
 */

import { useEffect, useRef, useSyncExternalStore } from 'react';
import styles from './notification-drawer.module.css';
// Carbon ikoner — referanse-drawer bruker et 16px ikon per varselrad
// (ikonet velges per event-type; chat → Chat-ikonet).
import { Chat as ChatIcon, Close as CloseIcon } from '@carbon/icons-react';
import {
  subscribeNotifications,
  getNotificationsSnapshot,
  getUnreadCountSnapshot,
  markAllNotificationsRead,
  type AppNotification,
} from '@/lib/notifications';

interface NotificationDrawerProps {
  /** Om drawer-en er åpen */
  open: boolean;
  /** Lukk-callback (Escape, X-knapp, klikk utenfor) */
  onClose: () => void;
}

/** Map notifikasjons-kind → ikon (per event-type, som referanse-drawer). */
function notificationIcon(kind: AppNotification['kind']) {
  // Foreløpig: chat/info → Chat-ikon. Flere typer legges til etter hvert
  // som backend får nye event-typer.
  return <ChatIcon size={16} />;
}

export default function NotificationDrawer({
  open,
  onClose,
}: NotificationDrawerProps) {
  // Live data fra notifications-store
  const notifications: AppNotification[] = useSyncExternalStore(
    subscribeNotifications,
    getNotificationsSnapshot
  );
  const unreadCount = useSyncExternalStore(
    subscribeNotifications,
    getUnreadCountSnapshot
  );

  // Fokus-håndtering: flytt fokus til drawer ved åpning, tilbake ved lukking
  const drawerRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    // Lagre fokusert element og flytt fokus til drawer-en
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    drawerRef.current?.focus();

    // Escape lukker drawer-en
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Returner fokus til triggeren (varsel-ikonet) ved lukking
      previouslyFocusedRef.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Skjerm-overlay — klikk utenfor lukker drawer-en (Carbon SidePanel-
          atferd). Klikkbart, men dekorativt for skjermlesere. */}
      <div
        className={styles.overlay}
        aria-hidden="true"
        onClick={onClose}
      />

      <div
        ref={drawerRef}
        className={styles.drawer}
        role="dialog"
        aria-modal="false"
        aria-label="Varsler"
        tabIndex={-1}
      >
        {/* 1. Tittelrad */}
        <div className={styles.titleRow}>
          <h2 className={styles.title}>Varsler</h2>
          <button
            type="button"
            className={styles.closeButton}
            aria-label="Lukk varsler"
            onClick={onClose}
          >
            <CloseIcon size={16} />
          </button>
        </div>

        {/* 2. Ulest-rad — kun når det finnes uleste */}
        {unreadCount > 0 && (
          <div className={styles.unreadRow}>
            <span className={styles.unreadCount}>{unreadCount} uleste</span>
            <button
              type="button"
              className={styles.markAllButton}
              onClick={markAllNotificationsRead}
            >
              Merk alle som lest
            </button>
          </div>
        )}

        {/* 3. Varselliste — role=log + aria-live: nye varsler annonseres */}
        <div
          className={styles.items}
          role="log"
          aria-live="polite"
          aria-relevant="additions"
        >
          {notifications.length === 0 ? (
            <p className={styles.empty}>Ingen nye varsler.</p>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={styles.item}
                /* NB: data-read må ALLTID renderes ('true'/'false') —
                   med `|| undefined` forsvant attributtet ved read=false,
                   og [data-read='false'] matchet aldri (blå border viste
                   seg aldri). */
                data-read={String(notification.read)}
              >
                {/* Ikon-kolonne — Carbon-ikon per event-type (referanse-
                    drawer: 16px ikon i egen kolonne til venstre). */}
                <div className={styles.itemIcon} aria-hidden="true">
                  {notificationIcon(notification.kind)}
                </div>
                {/* Body — subject (heading-compact-01) + caption (label-01) */}
                <div className={styles.itemBody}>
                  <span className={styles.itemSubject}>
                    {notification.title}
                  </span>
                  <span className={styles.itemCaption}>
                    {new Date(notification.createdAt).toLocaleDateString('nb-NO', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* 4. Footer — "Vis alle" (placeholder: lukker drawer inntil en
            dedikert varslingsside finnes) */}
        {notifications.length > 0 && (
          <div className={styles.footer}>
            <button
              type="button"
              className={styles.viewAllButton}
              onClick={onClose}
            >
              Vis alle
            </button>
          </div>
        )}
      </div>
    </>
  );
}
